import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import i18n from '../../../i18n';
import Utils from '../../../utils/utils';

import Home from '../../../models/Home';

import CompletionIndicator from '../../common/CompletionIndicator';
import EditPanel from '../../common/EditPanel';
import InputBedList from './InputBedList';
import {
    Callout,
    Text,
    CheckboxContainedGroup,
    CheckboxContained,
    Input,
    SelectField,
    Flex,
    CounterInput,
    Icon,
    Divider
} from '@homeexchange/design';

export default class HomeEditPanelHouseAndBeds extends EditPanel {
    static propTypes = {
        home: PropTypes.instanceOf(Home).isRequired,
        onChange: PropTypes.func.isRequired
    };

    static priority = 5;

    static title = i18n.t('home:housing_and_beds');

    constructor(props) {
        super(props);
        this.onSizeChange = this.onSizeChange.bind(this);
    }

    static checkValidity(props) {
        return {
            type: _.contains([Home.TYPE_HOME, Home.TYPE_APARTMENT], props.home.get('type')),
            residence_type: _.contains(
                [Home.RESIDENCE_PRIMARY, Home.RESIDENCE_SECONDARY],
                props.home.get('detail').residence_type
            ),
            beds:
                props.home.get('detail').single_bed +
                    props.home.get('detail').double_bed +
                    props.home.get('detail').big_double_bed +
                    props.home.get('detail').single_bed_up +
                    props.home.get('detail').double_bed_up >
                0,
            bedroom_nb:
                _.isNumber(props.home.get('detail').bedroom_nb) && props.home.get('detail').bedroom_nb >= 0,
            bathroom_nb:
                _.isNumber(props.home.get('detail').bathroom_nb) && props.home.get('detail').bathroom_nb > 0,
            size: _.isNumber(props.home.get('detail').size) && props.home.get('detail').size > 0
        };
    }

    onSizeChange(e) {
        this.props.home.get('detail').localized_size = e.target.value;
        this.props.onChange(e);
    }

    getCountOfAdults() {
        return (
            (this.props.home.get('detail').big_double_bed || 0) * 2 +
            (this.props.home.get('detail').single_bed || 0) +
            (this.props.home.get('detail').double_bed || 0) * 2 +
            (this.props.home.get('detail').double_bed_up || 0) * 2 +
            (this.props.home.get('detail').single_bed_up || 0)
        );
    }

    getCountOfChildren() {
        return (
            (this.props.home.get('detail').children_bed || 0) +
            (this.props.home.get('detail').children_bed_up || 0)
        );
    }

    getCountOfBabies() {
        return (
            (this.props.home.get('detail').baby_bed || 0) + (this.props.home.get('detail').baby_bed_up || 0)
        );
    }

    getTotalSleep() {
        return this.getCountOfAdults() + this.getCountOfChildren() + this.getCountOfBabies();
    }

    getSleepBySurface() {
        const detail = this.props.home.get('detail') || {};
        const size = Number(detail.size) || 0;
        const unit = detail.size_unit;

        const ratio = unit === Home.METRIC_SQFT ? 86.11 : 8; // 1 bed per 8 m² or 86.11 ft²
        return Math.floor(size / ratio);
    }

    static bedroomNbTransformer(value) {
        return value === 0 ? i18n.t('home:studio') : value;
    }

    getCalloutStatus() {
        const detail = this.props.home.get('detail') || {};
        const size = Number(detail.size) || 0;
        const unit = detail.size_unit;
        const bedroomNb = detail.bedroom_nb || 0;
        const totalGuests = this.getTotalSleep();
        const adults = this.getCountOfAdults();

        // min size for apply warning
        const minSize = unit === Home.METRIC_SQFT ? 91.5 : 8.5;

        if (totalGuests > 0 && adults === 0) {
            return 'error';
        }

        const maxGuestsForBedrooms = bedroomNb * 4 + 2;

        const isTooSmallToWarn = size < minSize;
        const isStudio = bedroomNb === 0;

        if (
            ((bedroomNb > 0 && totalGuests > maxGuestsForBedrooms) ||
                this.getSleepBySurface() < totalGuests) &&
            adults > 0 &&
            !isTooSmallToWarn &&
            !isStudio
        ) {
            return 'warning';
        }
    }

    renderSleepCount() {
        return (
            <>
                {this.getCountOfAdults() > 0 && (
                    <Text isSpan isBold>
                        {this.getCountOfAdults()}{' '}
                        {this.getCountOfAdults() > 1
                            ? i18n.t('home:home.beds.sleepingCapacity.calloutFilled.adults_other')
                            : i18n.t('home:home.beds.sleepingCapacity.calloutFilled.adults_one')}
                    </Text>
                )}
                {this.getCountOfChildren() > 0 && (
                    <>
                        {this.getCountOfAdults() > 0 && ' · '}
                        <Text isSpan isBold>
                            {this.getCountOfChildren()}{' '}
                            {this.getCountOfChildren() > 1
                                ? i18n.t('home:home.beds.sleepingCapacity.calloutFilled.children_other')
                                : i18n.t('home:home.beds.sleepingCapacity.calloutFilled.children_one')}
                        </Text>
                    </>
                )}
                {this.getCountOfBabies() > 0 && (
                    <>
                        {(this.getCountOfAdults() > 0 || this.getCountOfChildren() > 0) && ' · '}
                        <Text isSpan isBold>
                            {this.getCountOfBabies()}{' '}
                            {this.getCountOfBabies() > 1
                                ? i18n.t('home:home.beds.sleepingCapacity.calloutFilled.babies_other')
                                : i18n.t('home:home.beds.sleepingCapacity.calloutFilled.babies_one')}
                        </Text>
                    </>
                )}
            </>
        );
    }

    renderCalloutContent() {
        const detail = this.props.home.get('detail') || {};
        const size = Number(detail.size) || 0;
        const unit = detail.size_unit;
        const bedroomNb = detail.bedroom_nb || 0;
        const totalGuests = this.getTotalSleep();
        const adults = this.getCountOfAdults();
        const minSize = unit === Home.METRIC_SQFT ? 91.5 : 8.5;
        const isTooSmallToWarn = size < minSize;
        const isStudio = bedroomNb === 0;

        if (totalGuests > 0 && adults === 0) {
            return (
                <div>
                    <Text isSpan>{i18n.t('home:funnel.homeCreation.nbBeds.unfilledError')} </Text>
                </div>
            );
        }

        const maxGuestsForBedrooms = bedroomNb * 4 + 2;
        const totalMaxGuests = Math.min(maxGuestsForBedrooms, this.getSleepBySurface());

        if (
            bedroomNb > 0 &&
            (totalGuests > maxGuestsForBedrooms || this.getSleepBySurface() < totalGuests) &&
            adults > 0 &&
            !isTooSmallToWarn &&
            !isStudio
        ) {
            return (
                <div>
                    <Text isSpan isBold>
                        {i18n.t('home:home.beds.sleepingCapacity.calloutFilled.body')}{' '}
                    </Text>
                    {this.renderSleepCount()}
                    <Text
                        dangerouslySetInnerHTML={{
                            __html: i18n.t('home:home.beds.abuse.calloutBody', { nb: totalMaxGuests })
                        }}
                    />
                </div>
            );
        }

        if (totalGuests === 0) {
            return (
                <div>
                    <Text isSpan>{i18n.t('home:funnel.homeCreation.nbBeds.callout')} </Text>
                </div>
            );
        }

        return (
            <div>
                <Text isSpan>{i18n.t('home:home.beds.sleepingCapacity.calloutFilled.body')} </Text>
                {this.renderSleepCount()}
            </div>
        );
    }

    render() {
        const { home, onChange } = this.props;

        return (
            <fieldset className="panel panel-default" id={Utils.slugify(this.constructor.title)}>
                <div className="panel-heading">{i18n.t('home:housing_and_beds')}</div>
                <div className="panel-body">
                    <Flex direction="row" alignItems="center" gap="0">
                        <Text isBold>{i18n.t('home:home_type')}</Text>
                        <CompletionIndicator object={home} fieldNames={['type']} page="home" />
                    </Flex>
                    <CheckboxContainedGroup buttonWithDescription itemPerLine={2}>
                        <CheckboxContained
                            key="type-home"
                            icon="House"
                            label={i18n.t('home:funnel.homeCreation.homeType.house.title')}
                            description={i18n.t('home:funnel.homeCreation.homeType.house.desc')}
                            name="type"
                            type="radio"
                            value={Home.TYPE_HOME}
                            onChange={onChange}
                            checked={home.get('type') == Home.TYPE_HOME}
                            required
                        />
                        <CheckboxContained
                            key="type-apartment"
                            icon="Building"
                            label={i18n.t('home:funnel.homeCreation.homeType.flat.title')}
                            description={i18n.t('home:funnel.homeCreation.homeType.flat.desc')}
                            name="type"
                            type="radio"
                            value={Home.TYPE_APARTMENT}
                            onChange={onChange}
                            checked={home.get('type') == Home.TYPE_APARTMENT}
                            required
                        />
                    </CheckboxContainedGroup>
                    <Text isBold>{i18n.t('home:funnel.homeCreation.accomodationType.label')}</Text>
                    <CheckboxContainedGroup buttonWithDescription itemPerLine={2}>
                        <CheckboxContained
                            key="shared-part-whole-unit"
                            icon="House"
                            label={i18n.t('home:funnel.homeCreation.accomodationType.wholeUnit.title')}
                            description={i18n.t('home:funnel.homeCreation.accomodationType.wholeUnit.desc')}
                            name="shared_part"
                            type="radio"
                            value={Home.SHARED_PART_WHOLE_UNIT}
                            onChange={onChange}
                            checked={home.get('shared_part') === Home.SHARED_PART_WHOLE_UNIT}
                            required
                        />
                        <CheckboxContained
                            key="shared-part-private-room"
                            icon="HouseKey"
                            label={i18n.t('home:funnel.homeCreation.accomodationType.privateRoom.title')}
                            description={i18n.t('home:funnel.homeCreation.accomodationType.privateRoom.desc')}
                            name="shared_part"
                            type="radio"
                            value={Home.SHARED_PART_PRIVATE_ROOM}
                            onChange={onChange}
                            checked={home.get('shared_part') === Home.SHARED_PART_PRIVATE_ROOM}
                            required
                        />
                    </CheckboxContainedGroup>
                    <Flex direction="row" alignItems="center" gap="0">
                        <Text isBold>{i18n.t('home:residence_type')}</Text>
                        <CompletionIndicator object={home} fieldNames={['residence_type']} page="home" />
                    </Flex>
                    <CheckboxContainedGroup buttonWithDescription itemPerLine={2}>
                        <CheckboxContained
                            key="residence-type-primary"
                            icon="House"
                            label={i18n.t('home:funnel.homeCreation.residenceType.primary.title')}
                            description={i18n.t('home:funnel.homeCreation.residenceType.primary.desc')}
                            name="detail.residence_type"
                            type="radio"
                            value={Home.RESIDENCE_PRIMARY}
                            onChange={onChange}
                            checked={home.get('detail').residence_type == Home.RESIDENCE_PRIMARY}
                            required
                        />
                        <CheckboxContained
                            key="residence-type-secondary"
                            icon="HouseSmall"
                            label={i18n.t('home:funnel.homeCreation.residenceType.secondary.title')}
                            description={i18n.t('home:funnel.homeCreation.residenceType.secondary.desc')}
                            name="detail.residence_type"
                            type="radio"
                            value={Home.RESIDENCE_SECONDARY}
                            onChange={onChange}
                            checked={home.get('detail').residence_type == Home.RESIDENCE_SECONDARY}
                            required
                        />
                    </CheckboxContainedGroup>

                    <Flex direction="row" alignItems="center" gap="0">
                        <Text isBold>{i18n.t('home:size')}</Text>
                        <CompletionIndicator object={home} fieldNames={['size']} page="home" />
                    </Flex>
                    <div className="surface-container">
                        <Input
                            key="size"
                            id="size"
                            data-track-edition="surface_changed"
                            type="number"
                            name="detail.size"
                            value={home.get('detail').localized_size || ''}
                            onChange={this.onSizeChange}
                            min="0"
                            status={this.hasError('size') ? 'error' : 'default'}
                            helperText={i18n.t('home:informative_metric')}
                        />
                        <SelectField
                            key="size-unit"
                            name="detail.size_unit"
                            id="metric"
                            value={home.get('detail').size_unit || ''}
                            onChange={(value) =>
                                onChange(
                                    null,
                                    {},
                                    { name: 'detail.size_unit', value, track: 'surface_changed' }
                                )
                            }
                            required
                            items={[
                                {
                                    label: 'm&sup2;',
                                    value: Home.METRIC_M2
                                },
                                {
                                    label: 'ft&sup2;',
                                    value: Home.METRIC_SQFT
                                }
                            ]}
                            status={this.hasError('size') ? 'error' : 'default'}
                        />
                    </div>
                    <Flex direction="column" gap="4" isFullWidth>
                        <Flex direction="row" alignItems="center" gap="0">
                            <Text isBold>{i18n.t('home:funnel.homeCreation.nbRooms.label')}</Text>
                            <CompletionIndicator
                                object={home}
                                fieldNames={['bedroom_nb', 'bathroom_nb']}
                                page="home"
                            />
                        </Flex>
                        <Flex direction="column" gap="2" isFullWidth>
                            <div className={'counter-option'}>
                                <label className={'counter-option-label'}>
                                    <Icon size="large" name="Door" />
                                    <div className={'counter-option-label-text'}>
                                        {i18n.t('global:global.bedrooms')}
                                        <span className={'counter-option-description'}>
                                            {i18n.t('home:global.bedrooms.caption')}
                                        </span>
                                    </div>
                                </label>
                                <CounterInput
                                    minValue={0}
                                    maxValue={15}
                                    name="detail.bedroom_nb"
                                    onChange={(value) =>
                                        onChange(
                                            null,
                                            {},
                                            {
                                                name: 'detail.bedroom_nb',
                                                value,
                                                track: 'rooms_number_changed'
                                            }
                                        )
                                    }
                                    inputInitialValue={home.get('detail').bedroom_nb || 0}
                                    placeholderForZero={i18n.t('global:global.studio')}
                                />
                            </div>
                            <Flex direction="column" gap="0" isFullWidth>
                                <div
                                    className={classNames('counter-option', {
                                        'counter-option-error': this.hasError('bathroom_nb')
                                    })}
                                >
                                    <label className={'counter-option-label'}>
                                        <Icon size="large" name="Bathtub" />
                                        <div className={'counter-option-label-text'}>
                                            {i18n.t('global:global.bathrooms')}
                                        </div>
                                    </label>
                                    <CounterInput
                                        minValue={0}
                                        maxValue={15}
                                        name="detail.bathroom_nb"
                                        onChange={(value) =>
                                            onChange(
                                                null,
                                                {},
                                                {
                                                    name: 'detail.bathroom_nb',
                                                    value,
                                                    track: 'rooms_number_changed'
                                                }
                                            )
                                        }
                                        inputInitialValue={home.get('detail').bathroom_nb || 0}
                                        hasError={this.hasError('bathroom_nb')}
                                    />
                                </div>
                                {this.hasError('bathroom_nb') && (
                                    <span className="counter-option-error-text">
                                        {i18n.t('home:funnel.homeCreation.nbRooms.bedrooms.errorBlank')}
                                    </span>
                                )}
                            </Flex>
                        </Flex>
                    </Flex>
                    <Divider space="small" />
                    <Flex direction="column" gap="4" isFullWidth>
                        <Flex direction="row" alignItems="center" gap="0">
                            <Text isBold size="lg">
                                {i18n.t('home:funnel.homeCreation.nbBeds.label')}
                            </Text>
                            <CompletionIndicator object={home} fieldNames={['beds']} page="home" />
                        </Flex>
                        <Callout hasIcon isFullWidth status={this.getCalloutStatus()}>
                            {this.renderCalloutContent()}
                        </Callout>
                        <InputBedList home={home} onChange={onChange} />
                    </Flex>
                </div>
            </fieldset>
        );
    }
}
